import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { toApiError } from '../utils/api-error';

export interface GoalTypeDto {
  ID: number;
  Type: string;
  TypeName: string;
  Tip: string;
}

export interface CustomVariableDto {
  Name: string;
  DCR_ID?: string;
}

export interface CustomVariableValueDto {
  Value: string;
}

export interface GoalDto {
  ID: string;
  EntityType: string;
  EntityID: string;
  SourceGroupID?: string;
  ShowInDashboard: boolean;
  Description: string;
  Type: string;
  Rule: string;
  IsRegExp: boolean;
  Status: number;
  Preview?: string;
  CustomVarName?: string;
  CustomVarValue?: string;
  CustomVarOperator?: string;
  Details?: string;
  /** Calcolato lato API, non persistito: un'estrazione e' in corso su questo goal. */
  IsExtracting?: boolean;
  /** Calcolato lato API: un'estrazione e' partita ma non risulta mai conclusa (nightly incluso). */
  IsExtractionStalled?: boolean;
}

interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class GoalsApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getGoalTypes(): Observable<GoalTypeDto[]> {
    const endpoint = this.composeEndpoint('/webapp/crud/goals-type');
    return this.http.get<GenericResponse<GoalTypeDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[goals] types failed', error);
        return throwError(() => error);
      })
    );
  }

  getCustomVariables(): Observable<CustomVariableDto[]> {
    const endpoint = this.composeEndpoint('/webapp/crud/custom-variables');
    return this.http.get<GenericResponse<CustomVariableDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[goals] custom variables failed', error);
        return throwError(() => error);
      })
    );
  }

  getCustomVariableValues(variableName: string): Observable<CustomVariableValueDto[]> {
    const endpoint = this.composeEndpoint(`/webapp/crud/custom-variable-values?name=${encodeURIComponent(variableName)}`);
    return this.http.get<GenericResponse<CustomVariableValueDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(error => {
        console.error('[goals] custom variable values failed', error);
        return throwError(() => error);
      })
    );
  }

  saveCustomVariableValue(name: string, value: string): Observable<{ success: boolean }> {
    const endpoint = this.composeEndpoint('/webapp/crud/custom-variable-values');
    return this.http.post<GenericResponse<{ success: boolean }>>(
      endpoint.url,
      { name, value },
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => {
        if (!res.payload) {
          throw new Error('Unable to save custom variable value');
        }
        return res.payload;
      }),
      catchError(error => {
        console.error('[goals] save custom variable value failed', error);
        return throwError(() => error);
      })
    );
  }

  getGoals(): Observable<GoalDto[]> {
    const endpoint = this.composeEndpoint('/webapp/crud/goals');
    return this.http.get<GenericResponse<GoalDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => this.parseCustomVariables(res.payload ?? [])),
      catchError(error => {
        console.error('[goals] list failed', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Stesso endpoint della lista, filtrato a un solo goal via GoalID. Pensato per il
   * polling post-estrazione (goals.component.ts): rifare getGoals() a ogni tentativo
   * scaricherebbe l'intera lista solo per controllare una riga.
   */
  getGoal(goalId: string): Observable<GoalDto | undefined> {
    const endpoint = this.composeEndpoint(`/webapp/crud/goals?GoalID=${encodeURIComponent(goalId)}`);
    return this.http.get<GenericResponse<GoalDto[]>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => this.parseCustomVariables(res.payload ?? [])[0]),
      catchError(error => {
        console.error('[goals] single fetch failed', error);
        return throwError(() => error);
      })
    );
  }

  /** Deriva CustomVarName/Value/Operator dal campo Preview, in place. Condiviso da getGoals/getGoal. */
  private parseCustomVariables(goals: GoalDto[]): GoalDto[] {
    goals.forEach((g: GoalDto) => {
      if (g.Type === 'customvariablelist' && g.Preview) {
        try {
          const parsed = JSON.parse(g.Preview);
          g.CustomVarName = parsed.Name;
          g.CustomVarValue = parsed.Value;
          g.CustomVarOperator = parsed.Operator;
        } catch (e) {
          console.error('[goals] failed to parse custom variable preview', e);
        }
      }
    });
    return goals;
  }

  createGoal(payload: Partial<GoalDto>): Observable<GoalDto> {
    const endpoint = this.composeEndpoint('/webapp/crud/goals');
    return this.http.post<GenericResponse<GoalDto>>(
      endpoint.url,
      payload,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => {
        if (!res.payload) {
          throw new Error('Unable to create goal');
        }
        return res.payload;
      }),
      catchError(error => {
        console.error('[goals] create failed', error);
        return throwError(() => error);
      })
    );
  }

  updateGoal(payload: Partial<GoalDto>): Observable<void> {
    const endpoint = this.composeEndpoint('/webapp/crud/goals');
    return this.http.put<GenericResponse<string>>(
      endpoint.url,
      payload,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => {
        if (res.error) {
          throw new Error(res.error.message || 'Unable to update goal');
        }
      }),
      catchError(error => {
        console.error('[goals] update failed', error);
        return throwError(() => error);
      })
    );
  }

  deleteGoal(goalId: string): Observable<void> {
    const endpoint = this.composeEndpoint(`/webapp/crud/goals?goalID=${goalId}`);
    return this.http.delete<GenericResponse<GoalDto>>(
      endpoint.url,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(() => undefined),
      catchError(error => {
        console.error('[goals] delete failed', error);
        return throwError(() => error);
      })
    );
  }

  recalculateGoal(goalId: string): Observable<boolean> {
    const endpoint = this.composeEndpoint('/webapp/crud/goals/recalculate');
    const body = {
      goalID: goalId,
      daysBack: 90
    };
    return this.http.post<GenericResponse<any>>(
      endpoint.url,
      body,
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => {
        if (res.error) {
          throw new Error(res.error.message || 'Unable to recalculate goal');
        }
        return true;
      }),
      catchError(error => {
        console.error('[goals] recalculate failed', error);
        // Es. goal di un'entity sandbox (HTTP 409): il messaggio dell'API spiega il rifiuto.
        return throwError(() => toApiError(error));
      })
    );
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) {
      path = `/${path}`;
    }
    return {
      url: `${info.baseUrl}${path}`,
      useRemote: info.useRemote
    };
  }

  private buildHeaders(useRemote: boolean): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    if (useRemote) {
      const token = this.authService.getTncApiToken();
      if (token) {
        headers = headers.set('Authorization', token);
      }
    } else {
      headers = new HttpHeaders(this.authService.getAuthHeaders());
      headers = headers.set('Content-Type', 'application/json');
    }

    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/+$/u, '');
  }
}
